"use client";

import { ChangeEvent, FormEvent, useEffect, useMemo, useRef, useState } from "react";

type Role = "教师" | "学生";
type AuthRole = "管理员" | "教师" | "学生" | "待审核";
type Status = "在岗" | "请假" | "未打卡";
type Person = {
  id: string;
  name: string;
  role: Role;
  unit: string;
  status: Status;
  checkIn: string;
  rate: number;
  leaveDays: number;
  leaveType?: string;
  leavePeriod?: string;
  advisorId?: string;
  advisorName?: string;
};
type ApprovalStatus = "待教师审批" | "已批准" | "已驳回";
type LeaveRequest = {
  id: number;
  personId: string;
  personName: string;
  role: Role;
  unit: string;
  type: string;
  start: string;
  end: string;
  reason: string;
  approverId: string;
  approverName: string;
  approvalStatus: ApprovalStatus;
  createdAt: string;
};
type AuthUser = { id: string; name: string; avatarUrl?: string; role: AuthRole; personId: string | null };

const teacherNames = [
  "王静", "李明远", "周倩", "陈思源", "赵文博", "孙晓楠", "刘哲", "杨舒", "何嘉", "郑琳",
  "高远", "林致远", "吴桐", "宋佳", "徐宁", "冯悦", "马骁", "唐婉", "曹磊", "许诺",
];
const studentNames = [
  "张子墨", "李欣妍", "王浩然", "陈雨桐", "刘宇航", "赵一诺", "杨晨曦", "周嘉乐", "孙语嫣", "黄俊熙",
  "吴思涵", "徐博文", "胡可欣", "朱星宇", "高梓萱", "林瑞", "何梦琪", "郭昊", "罗佳怡", "宋泽宇",
  "谢雨欣", "唐皓轩", "韩依然", "曹嘉豪", "冯诗涵", "邓子涵", "彭宇辰", "曾安然", "萧景行", "叶知秋",
  "苏念", "蒋昕", "杜若", "陆一川", "方予安", "程亦可", "沈星河", "白露", "乔木", "顾清和",
];
const cohorts = ["2025级", "2026级"];

const seededPeople: Person[] = [
  ...teacherNames.map((name, i): Person => ({
    id: `T${String(i + 1).padStart(4, "0")}`,
    name,
    role: "教师",
    unit: "团队教师",
    status: i === 2 || i === 9 ? "请假" : i === 16 ? "未打卡" : "在岗",
    checkIn: i === 2 || i === 9 ? "—" : `0${7 + (i % 2)}:${String(38 + (i * 7) % 20).padStart(2, "0")}`,
    rate: 96 + ((i * 13) % 39) / 10,
    leaveDays: i === 2 ? 1 : i === 9 ? 3 : i % 8 === 0 ? 1 : 0,
    leaveType: i === 2 ? "事假" : i === 9 ? "病假" : undefined,
    leavePeriod: i === 2 ? "08.11 上午" : i === 9 ? "08.10—08.12" : undefined,
  })),
  ...studentNames.map((name, i): Person => ({
    id: `S2026${String(i + 1).padStart(4, "0")}`,
    name,
    role: "学生",
    unit: cohorts[i % cohorts.length],
    status: i % 17 === 2 || i % 19 === 6 ? "请假" : i % 23 === 8 ? "未打卡" : "在岗",
    checkIn: i % 17 === 2 || i % 19 === 6 ? "—" : `0${7 + (i % 2)}:${String(31 + (i * 9) % 27).padStart(2, "0")}`,
    rate: 91 + ((i * 17) % 86) / 10,
    leaveDays: i % 17 === 2 ? 2 : i % 19 === 6 ? 1 : i % 13 === 0 ? 1 : 0,
    leaveType: i % 17 === 2 ? "病假" : i % 19 === 6 ? "事假" : undefined,
    leavePeriod: i % 17 === 2 ? "08.11—08.12" : i % 19 === 6 ? "08.11 全天" : undefined,
  })),
];

const seededRequests: LeaveRequest[] = [
  { id: 101, personId: "S20260003", personName: "王浩然", role: "学生", unit: "2025级", type: "病假", start: "2026-08-11", end: "2026-08-12", reason: "发热就医", approverId: "T0003", approverName: "周倩", approvalStatus: "待教师审批", createdAt: "2026-08-11 08:46" },
  { id: 102, personId: "S20260007", personName: "杨晨曦", role: "学生", unit: "2026级", type: "事假", start: "2026-08-11", end: "2026-08-11", reason: "办理证件", approverId: "T0003", approverName: "周倩", approvalStatus: "待教师审批", createdAt: "2026-08-11 08:20" },
  { id: 103, personId: "S20260018", personName: "郭昊", role: "学生", unit: "2025级", type: "病假", start: "2026-08-10", end: "2026-08-11", reason: "身体不适", approverId: "T0002", approverName: "李明远", approvalStatus: "已批准", createdAt: "2026-08-10 19:15" },
  { id: 104, personId: "S20260025", personName: "冯诗涵", role: "学生", unit: "2026级", type: "事假", start: "2026-08-09", end: "2026-08-09", reason: "个人事务", approverId: "T0001", approverName: "王静", approvalStatus: "已驳回", createdAt: "2026-08-09 09:03" },
];

const nav = [
  { id: "dashboard", label: "工作台", icon: "⌂" },
  { id: "onduty", label: "在岗人员", icon: "✓" },
  { id: "leave", label: "请假管理", icon: "◷" },
  { id: "statistics", label: "统计分析", icon: "▥" },
  { id: "roster", label: "人员名单", icon: "◎" },
  { id: "users", label: "身份管理", icon: "♙" },
];

const trend = [92.8, 94.1, 93.6, 95.2, 96.4, 95.8, 97.1, 96.7];
const weekDays = ["周一", "周二", "周三", "周四", "周五", "周六", "周日"];
const API_BASE = ((import.meta as ImportMeta & { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE || "").replace(/\/$/, "");

function initials(name: string) {
  return name.slice(-2);
}

function StatusTag({ status }: { status: Status }) {
  return <span className={`status status-${status}`}>{status}</span>;
}

function MiniBars({ values }: { values: number[] }) {
  return (
    <div className="mini-bars" aria-label="近八周出勤率趋势图">
      {values.map((value, index) => (
        <div className="mini-bar-wrap" key={index}>
          <span className="mini-value">{value}%</span>
          <div className="mini-track">
            <span className="mini-bar" style={{ height: `${Math.max(18, (value - 88) * 7)}%` }} />
          </div>
          <small>{index + 1}周</small>
        </div>
      ))}
    </div>
  );
}

export default function Home() {
  const [authLoading, setAuthLoading] = useState(true);
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [wechatEnabled, setWechatEnabled] = useState(false);
  const [authError, setAuthError] = useState("");
  const [active, setActive] = useState("dashboard");
  const [people, setPeople] = useState<Person[]>(seededPeople);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>(seededRequests);
  const [roleFilter, setRoleFilter] = useState<"全部" | Role>("全部");
  const [statusFilter, setStatusFilter] = useState<"全部" | Status>("全部");
  const [query, setQuery] = useState("");
  const [period, setPeriod] = useState("2026年8月");
  const [modalOpen, setModalOpen] = useState(false);
  const [toast, setToast] = useState("");
  const [mobileNav, setMobileNav] = useState(false);
  const importRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setAuthError(params.get("auth_error") || "");
    Promise.all([
      fetch(`${API_BASE}/api/auth/config`, { credentials: "include" }).then((response) => response.json()),
      fetch(`${API_BASE}/api/auth/me`, { credentials: "include" }).then((response) => response.json()),
    ]).then(([config, session]) => {
      setWechatEnabled(Boolean(config.enabled));
      setAuthUser(session.user || null);
    }).catch(() => setAuthError("无法连接到登录服务，请检查服务器状态")).finally(() => setAuthLoading(false));
  }, []);

  useEffect(() => {
    if (!authUser || authUser.role === "待审核") return;
    const controller = new AbortController();
    fetch(`${API_BASE}/api/people`, { signal: controller.signal, credentials: "include" })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error("API unavailable")))
      .then((payload) => {
        if (Array.isArray(payload.people) && payload.people.length) setPeople(payload.people);
      })
      .catch(() => undefined);
    fetch(`${API_BASE}/api/leave`, { signal: controller.signal, credentials: "include" })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error("API unavailable")))
      .then((payload) => {
        if (Array.isArray(payload.requests) && payload.requests.length) setLeaveRequests(payload.requests);
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [authUser]);

  const filtered = useMemo(() => {
    return people.filter((person) => {
      const sectionStatus = active === "onduty" ? "在岗" : active === "leave" ? "请假" : statusFilter;
      return (roleFilter === "全部" || person.role === roleFilter)
        && (sectionStatus === "全部" || person.status === sectionStatus)
        && (!query || `${person.name}${person.id}${person.unit}`.toLowerCase().includes(query.toLowerCase()));
    });
  }, [active, people, query, roleFilter, statusFilter]);

  const totals = useMemo(() => {
    const activeCount = people.filter((p) => p.status === "在岗").length;
    const leaveCount = people.filter((p) => p.status === "请假").length;
    const teachers = people.filter((p) => p.role === "教师");
    const students = people.filter((p) => p.role === "学生");
    const teacherRate = teachers.reduce((sum, p) => sum + p.rate, 0) / Math.max(teachers.length, 1);
    const studentRate = students.reduce((sum, p) => sum + p.rate, 0) / Math.max(students.length, 1);
    return { activeCount, leaveCount, teacherRate, studentRate };
  }, [people]);

  function notify(message: string) {
    setToast(message);
    window.setTimeout(() => setToast(""), 2800);
  }

  function importCsv(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const rows = String(reader.result).split(/\r?\n/).filter(Boolean).slice(1);
      const additions = rows.map((row, index): Person | null => {
        const [name, roleText, unit, id] = row.split(",").map((item) => item.trim());
        if (!name || (roleText !== "教师" && roleText !== "学生")) return null;
        return {
          id: id || `I${Date.now()}${index}`,
          name,
          role: roleText,
          unit: unit || (roleText === "教师" ? "团队教师" : "2026级"),
          status: "未打卡",
          checkIn: "—",
          rate: 100,
          leaveDays: 0,
        };
      }).filter((item): item is Person => Boolean(item));
      setPeople((current) => [...additions, ...current]);
      try {
        await fetch(`${API_BASE}/api/people/import`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ people: additions }),
        });
      } catch { /* Offline preview keeps imported rows in this session. */ }
      notify(`已导入 ${additions.length} 名人员`);
      event.target.value = "";
    };
    reader.readAsText(file, "utf-8");
  }

  async function submitLeave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const id = authUser?.role === "学生" ? String(authUser.personId || "") : String(data.get("person"));
    const type = String(data.get("type"));
    const approverId = String(data.get("approver"));
    const periodText = `${data.get("start")}—${data.get("end")}`;
    let savedRequest: LeaveRequest | undefined;
    try {
      const response = await fetch(`${API_BASE}/api/leave`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ personId: id, approverId, type, start: data.get("start"), end: data.get("end"), reason: data.get("reason") }),
      });
      if (!response.ok) throw new Error("Save failed");
      const payload = await response.json();
      savedRequest = payload.request;
    } catch { notify("提交失败，请检查网络后重试"); return; }
    const applicant = people.find((p) => p.id === id);
    const selectedApprover = people.find((p) => p.id === approverId && p.role === "教师");
    if (savedRequest) setLeaveRequests((current) => [savedRequest!, ...current]);
    else if (applicant) setLeaveRequests((current) => [{
      id: Date.now(), personId: id, personName: applicant.name, role: applicant.role, unit: applicant.unit,
      type, start: String(data.get("start")), end: String(data.get("end")), reason: String(data.get("reason") || ""),
      approverId: selectedApprover?.id || "T0001", approverName: selectedApprover?.name || "王静",
      approvalStatus: "待教师审批", createdAt: "刚刚",
    }, ...current]);
    setModalOpen(false);
    notify(`申请已提交给${savedRequest?.approverName || selectedApprover?.name || "所选教师"}审批`);
  }

  async function decideLeave(id: number, decision: "approve" | "reject") {
    const request = leaveRequests.find((item) => item.id === id);
    if (!request) return;
    try {
      await fetch(`${API_BASE}/api/leave/${id}/decision`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ decision, approverId: request.approverId }),
      });
    } catch { notify("审批提交失败，请检查网络后重试"); return; }
    const approvalStatus: ApprovalStatus = decision === "approve" ? "已批准" : "已驳回";
    setLeaveRequests((current) => current.map((item) => item.id === id ? { ...item, approvalStatus } : item));
    if (decision === "approve") setPeople((current) => current.map((person) => person.id === request.personId ? { ...person, status: "请假", checkIn: "—", leaveType: request.type, leavePeriod: `${request.start}—${request.end}`, leaveDays: person.leaveDays + 1 } : person));
    notify(decision === "approve" ? "已批准并计入统一考勤汇总" : "已驳回申请");
  }

  async function logout() {
    await fetch(`${API_BASE}/api/auth/logout`, { method: "POST", credentials: "include" }).catch(() => undefined);
    window.location.reload();
  }

  if (authLoading) return <AuthLoading />;
  if (!authUser) return <LoginScreen enabled={wechatEnabled} error={authError} />;
  if (authUser.role === "待审核") return <PendingScreen user={authUser} onLogout={logout} />;

  const visibleNav = authUser.role === "管理员"
    ? nav
    : authUser.role === "教师"
      ? nav.filter((item) => ["dashboard", "onduty", "leave", "statistics"].includes(item.id))
      : nav.filter((item) => ["dashboard", "leave"].includes(item.id));
  const sectionTitle = visibleNav.find((item) => item.id === active)?.label || "工作台";

  return (
    <main className="app-shell">
      <aside className={`sidebar ${mobileNav ? "sidebar-open" : ""}`}>
        <div className="brand">
          <div className="brand-mark"><span>勤</span></div>
          <div><strong>遥感大数据<br />智能计算团队</strong><small>Attendance Hub</small></div>
        </div>
        <div className="school-pill"><span>团队考勤平台</span><b>{authUser.role}端</b></div>
        <nav className="main-nav" aria-label="主菜单">
          <p>日常管理</p>
          {visibleNav.slice(0, 3).map((item) => (
            <button key={item.id} className={active === item.id ? "active" : ""} onClick={() => { setActive(item.id); setMobileNav(false); }}>
              <i>{item.icon}</i><span>{item.label}</span>{item.id === "leave" && <em>{totals.leaveCount}</em>}
            </button>
          ))}
          <p>数据与设置</p>
          {visibleNav.slice(3).map((item) => (
            <button key={item.id} className={active === item.id ? "active" : ""} onClick={() => { setActive(item.id); setMobileNav(false); }}>
              <i>{item.icon}</i><span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-help">
          <span>?</span><div><strong>使用帮助</strong><small>查看操作指南与口径说明</small></div>
        </div>
        <div className="profile">
          <div className="avatar avatar-admin">{initials(authUser.name)}</div>
          <div><strong>{authUser.name}</strong><small>{authUser.role}</small></div>
          <button className="text-button" onClick={logout}>退出</button>
        </div>
      </aside>

      <section className="workspace">
        <header className="topbar">
          <button className="mobile-menu" aria-label="打开菜单" onClick={() => setMobileNav(!mobileNav)}>☰</button>
          <div>
            <h1>{sectionTitle}</h1>
            <p>遥感大数据智能计算团队 · 2026</p>
          </div>
          <div className="top-actions">
            <label className="global-search"><span>⌕</span><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="搜索姓名、编号或入学年级" /></label>
            <button className="icon-button" aria-label="消息">♧<b>3</b></button>
            {(authUser.role === "管理员" || authUser.role === "学生") && <button className="primary" onClick={() => setModalOpen(true)}><span>＋</span> {authUser.role === "学生" ? "提交请假" : "发起登记"}</button>}
          </div>
        </header>

        <div className="content">
          {active === "dashboard" && (
            <>
              <section className="welcome-row">
                <div><p className="eyebrow">8月11日 · 星期二</p><h2>上午好，团队管理员</h2><p>今日共有 <b>{totals.leaveCount}</b> 人请假，<b>{people.filter((p) => p.status === "未打卡").length}</b> 人尚未完成打卡。</p></div>
                <div className="time-chip"><span className="pulse" />考勤数据已更新 <b>09:28</b></div>
              </section>

              <section className="metric-grid">
                <article className="metric-card featured"><div className="metric-icon">人</div><div><span>团队成员</span><strong>{people.length}</strong><small>教师 {people.filter((p) => p.role === "教师").length} · 学生 {people.filter((p) => p.role === "学生").length}</small></div><em>今日</em></article>
                <article className="metric-card"><div className="metric-icon green">✓</div><div><span>当前在岗</span><strong>{totals.activeCount}</strong><small className="up">实时考勤状态</small></div><em>{((totals.activeCount / people.length) * 100).toFixed(1)}%</em></article>
                <article className="metric-card"><div className="metric-icon amber">◷</div><div><span>请假人数</span><strong>{totals.leaveCount}</strong><small>教师 {people.filter((p) => p.role === "教师" && p.status === "请假").length} · 学生 {people.filter((p) => p.role === "学生" && p.status === "请假").length}</small></div><em>{((totals.leaveCount / people.length) * 100).toFixed(1)}%</em></article>
                <article className="metric-card"><div className="metric-icon coral">!</div><div><span>异常待处理</span><strong>{people.filter((p) => p.status === "未打卡").length}</strong><small>未打卡成员</small></div><em>需关注</em></article>
              </section>

              <section className="dashboard-grid">
                <article className="panel attendance-panel">
                  <div className="panel-head"><div><h3>本月出勤率</h3><p>按师生身份统计的有效出勤</p></div><select value={period} onChange={(e) => setPeriod(e.target.value)}><option>2026年8月</option><option>2026年7月</option><option>2026年6月</option></select></div>
                  <div className="rate-overview">
                    <div className="donut" style={{ "--rate": "96.2%" } as React.CSSProperties}><div><strong>96.2%</strong><span>整体出勤率</span></div></div>
                    <div className="rate-details">
                      <div><span><i className="dot blue" />教师</span><strong>{totals.teacherRate.toFixed(1)}%</strong><small>较上月 +0.8%</small></div>
                      <div><span><i className="dot mint" />学生</span><strong>{totals.studentRate.toFixed(1)}%</strong><small>较上月 +1.3%</small></div>
                    </div>
                  </div>
                  <div className="spark-row"><span>近八周</span><div className="sparkline">{trend.map((n, i) => <i key={i} style={{ height: `${(n - 89) * 4 + 10}px` }} />)}</div><b>稳步上升</b></div>
                </article>

                <article className="panel today-panel">
                  <div className="panel-head"><div><h3>今日状态</h3><p>实时统计 · 每 5 分钟刷新</p></div><button className="text-button" onClick={() => setActive("onduty")}>查看全部 →</button></div>
                  <div className="today-list">
                    <div><span className="ring ring-green">{((totals.activeCount / people.length) * 100).toFixed(0)}%</span><p><strong>在岗</strong><small>{totals.activeCount} 人</small></p><b>正常</b></div>
                    <div><span className="ring ring-amber">{((totals.leaveCount / people.length) * 100).toFixed(1)}%</span><p><strong>请假</strong><small>{totals.leaveCount} 人</small></p><b>已审批</b></div>
                    <div><span className="ring ring-red">{((people.filter((p) => p.status === "未打卡").length / people.length) * 100).toFixed(1)}%</span><p><strong>异常</strong><small>{people.filter((p) => p.status === "未打卡").length} 人</small></p><b>待处理</b></div>
                  </div>
                </article>

                <article className="panel roster-preview">
                  <div className="panel-head"><div><h3>实时人员动态</h3><p>最近更新的考勤状态</p></div><div className="segmented"><button className={roleFilter === "全部" ? "active" : ""} onClick={() => setRoleFilter("全部")}>全部</button><button className={roleFilter === "教师" ? "active" : ""} onClick={() => setRoleFilter("教师")}>教师</button><button className={roleFilter === "学生" ? "active" : ""} onClick={() => setRoleFilter("学生")}>学生</button></div></div>
                  <div className="people-table compact-table">
                    <div className="table-head"><span>人员</span><span>成员组</span><span>打卡时间</span><span>状态</span></div>
                    {filtered.slice(0, 6).map((person, i) => <PersonRow person={person} key={person.id} index={i} />)}
                  </div>
                </article>

                <article className="panel alert-panel">
                  <div className="panel-head"><div><h3>待办提醒</h3><p>需要你处理的事项</p></div><span className="badge">4 项</span></div>
                  <button><i className="alert-icon amber">审</i><span><strong>请假审批待处理</strong><small>3 份申请等待审核</small></span><b>3</b></button>
                  <button><i className="alert-icon coral">异</i><span><strong>异常考勤核对</strong><small>2026级 · 4 人</small></span><b>4</b></button>
                  <button><i className="alert-icon blue-bg">报</i><span><strong>7 月月报已生成</strong><small>可查看并导出分析报告</small></span><em>查看</em></button>
                </article>
              </section>
            </>
          )}

          {(active === "onduty" || active === "roster") && (
            <section className="panel list-page">
              <div className="list-title">
                <div><span className="eyebrow">人员台账</span><h2>{active === "onduty" ? "今日在岗人员" : "教师与学生名单"}</h2><p>当前展示 {filtered.length} 条模拟记录，正式名单可随时批量导入。</p></div>
                <div className="list-actions">
                  <input ref={importRef} hidden type="file" accept=".csv" onChange={importCsv} />
                  <button className="secondary" onClick={() => importRef.current?.click()}>⇧ 导入 CSV</button>
                  <button className="primary" onClick={() => setModalOpen(true)}>＋ 请假登记</button>
                </div>
              </div>
              <div className="filters">
                <label><span>⌕</span><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="搜索姓名、编号或入学年级" /></label>
                <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value as "全部" | Role)}><option>全部</option><option>教师</option><option>学生</option></select>
                {active === "roster" && <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as "全部" | Status)}><option>全部</option><option>在岗</option><option>请假</option><option>未打卡</option></select>}
                <span className="result-count">共 {filtered.length} 人</span>
              </div>
              <div className="people-table full-table">
                <div className="table-head"><span>姓名 / 编号</span><span>身份</span><span>成员组</span><span>打卡时间</span><span>本月出勤率</span><span>状态</span></div>
                {filtered.map((person, i) => <PersonRow person={person} key={person.id} index={i} full />)}
                {filtered.length === 0 && <div className="empty-state"><span>⌕</span><strong>没有找到匹配人员</strong><small>请调整搜索词或筛选条件</small></div>}
              </div>
            </section>
          )}

          {active === "leave" && <LeaveApprovalPage requests={leaveRequests} onDecision={decideLeave} onCreate={() => setModalOpen(true)} canCreate={authUser.role === "管理员" || authUser.role === "学生"} canDecide={authUser.role === "管理员" || authUser.role === "教师"} isTeacher={authUser.role === "教师"} />}

          {active === "statistics" && <Statistics people={people} period={period} setPeriod={setPeriod} />}
          {active === "users" && authUser.role === "管理员" && <UserManagement people={people} />}
        </div>
      </section>

      {modalOpen && (
        <div className="modal-backdrop" onMouseDown={() => setModalOpen(false)}>
          <form className="modal" onSubmit={submitLeave} onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-head"><div><span className="eyebrow">ATTENDANCE RECORD</span><h2>新建请假登记</h2></div><button type="button" onClick={() => setModalOpen(false)}>×</button></div>
            <label>申请人员{authUser.role === "学生" ? <input value={`${authUser.name} · ${authUser.personId || "待绑定"}`} readOnly /> : <select name="person" required defaultValue=""><option value="" disabled>搜索或选择人员</option>{people.slice(0, 80).map((p) => <option value={p.id} key={p.id}>{p.name} · {p.id} · {p.role}</option>)}</select>}</label>
            <div className="form-row"><label>请假类型<select name="type"><option>事假</option><option>病假</option><option>公假</option><option>其他</option></select></label><label>选择审批教师<select name="approver" required defaultValue=""><option value="" disabled>请选择教师</option>{people.filter((p) => p.role === "教师").map((p) => <option value={p.id} key={p.id}>{p.name} · {p.unit}</option>)}</select></label></div>
            <div className="form-row"><label>开始日期<input type="date" name="start" required defaultValue="2026-08-11" /></label><label>结束日期<input type="date" name="end" required defaultValue="2026-08-11" /></label></div>
            <label>说明<textarea name="reason" rows={3} placeholder="填写请假事由（选填）" /></label>
            <div className="modal-actions"><button type="button" className="secondary" onClick={() => setModalOpen(false)}>取消</button><button className="primary" type="submit">保存登记</button></div>
          </form>
        </div>
      )}
      {toast && <div className="toast"><span>✓</span>{toast}</div>}
      {mobileNav && <div className="nav-backdrop" onClick={() => setMobileNav(false)} />}
    </main>
  );
}

function AuthLoading() {
  return <main className="auth-screen"><div className="auth-card"><div className="brand-mark"><span>勤</span></div><h1>正在检查登录状态</h1><p>请稍候…</p></div></main>;
}

function LoginScreen({ enabled, error }: { enabled: boolean; error: string }) {
  return <main className="auth-screen"><div className="auth-card">
    <div className="brand-mark"><span>勤</span></div>
    <p className="eyebrow">REMOTE SENSING TEAM</p><h1>遥感大数据智能计算团队</h1>
    <p>考勤与请假管理平台</p>
    {error && <div className="auth-error">{error}</div>}
    {enabled ? <a className="wechat-login" href={`${API_BASE}/api/auth/wechat/start`}>微信扫码登录</a> : <div className="auth-error">管理员尚未配置微信开放平台参数，请联系系统管理员。</div>}
    <small>首次扫码会自动注册身份信息；待管理员确认角色后即可使用。</small>
  </div></main>;
}

function PendingScreen({ user, onLogout }: { user: AuthUser; onLogout: () => void }) {
  return <main className="auth-screen"><div className="auth-card"><div className="brand-mark"><span>勤</span></div><h1>账号待管理员确认</h1><p>微信账号“{user.name}”已注册，但尚未分配教师或学生身份。</p><p>请联系团队管理员完成身份绑定后重新登录。</p><button className="secondary" onClick={onLogout}>退出登录</button></div></main>;
}

type ManagedUser = { id: string; name: string; role: AuthRole; personId: string | null; personName: string | null };

function UserManagement({ people }: { people: Person[] }) {
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [message, setMessage] = useState("");
  useEffect(() => { fetch(`${API_BASE}/api/admin/users`, { credentials: "include" }).then((response) => response.json()).then((payload) => setUsers(payload.users || [])).catch(() => setMessage("用户列表加载失败")); }, []);
  async function update(userId: string, role: AuthRole, personId: string) {
    const response = await fetch(`${API_BASE}/api/admin/users/${encodeURIComponent(userId)}/role`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ role, personId }) });
    if (!response.ok) { setMessage((await response.json().catch(() => ({ error: "保存失败" }))).error); return; }
    setUsers((current) => current.map((item) => item.id === userId ? { ...item, role, personId: role === "待审核" || role === "管理员" ? null : personId, personName: people.find((p) => p.id === personId)?.name || null } : item));
    setMessage("身份已更新");
  }
  return <section className="panel list-page"><div className="list-title"><div><span className="eyebrow">IDENTITY MANAGEMENT</span><h2>身份与权限</h2><p>微信扫码用户首次注册后在此绑定团队身份。</p></div>{message && <span className="badge">{message}</span>}</div><div className="people-table full-table"><div className="table-head"><span>微信用户</span><span>当前角色</span><span>绑定人员</span><span>操作</span></div>{users.map((item) => <div className="person-row" key={item.id}><span className="person-cell"><i className="avatar avatar-1">{initials(item.name)}</i><span><strong>{item.name}</strong><small>{item.id}</small></span></span><span><select defaultValue={item.role} id={`role-${item.id}`}><option>待审核</option><option>管理员</option><option>教师</option><option>学生</option></select></span><span><select defaultValue={item.personId || ""} id={`person-${item.id}`}><option value="">不绑定</option>{people.map((person) => <option key={person.id} value={person.id}>{person.name} · {person.id} · {person.role}</option>)}</select></span><span><button className="secondary" onClick={() => { const role = (document.getElementById(`role-${item.id}`) as HTMLSelectElement).value as AuthRole; const personId = (document.getElementById(`person-${item.id}`) as HTMLSelectElement).value; void update(item.id, role, personId); }}>保存</button></span></div>)}{users.length === 0 && <div className="empty-state"><strong>暂无扫码用户</strong><small>用户首次扫码登录后会显示在这里</small></div>}</div></section>;
}

function PersonRow({ person, index, full = false, leaveView = false }: { person: Person; index: number; full?: boolean; leaveView?: boolean }) {
  return (
    <div className="person-row">
      <span className="person-cell"><i className={`avatar avatar-${index % 6}`}>{initials(person.name)}</i><span><strong>{person.name}</strong><small>{person.id}</small></span></span>
      {full && <span><i className={`role-dot ${person.role === "教师" ? "teacher" : "student"}`} />{person.role}</span>}
      <span>{person.unit}</span>
      <span>{leaveView ? <><b className="leave-type">{person.leaveType || "其他"}</b><small className="subline">{person.leavePeriod || "08.11 全天"}</small></> : person.checkIn}</span>
      {full && <span className="rate-cell"><b>{person.rate.toFixed(1)}%</b><i><em style={{ width: `${person.rate}%` }} /></i></span>}
      <span><StatusTag status={person.status} /></span>
    </div>
  );
}

function LeaveApprovalPage({ requests, onDecision, onCreate, canCreate, canDecide, isTeacher }: { requests: LeaveRequest[]; onDecision: (id: number, decision: "approve" | "reject") => void; onCreate: () => void; canCreate: boolean; canDecide: boolean; isTeacher: boolean }) {
  const [approvalFilter, setApprovalFilter] = useState<"全部" | ApprovalStatus>("全部");
  const visible = approvalFilter === "全部" ? requests : requests.filter((item) => item.approvalStatus === approvalFilter);
  const pending = requests.filter((item) => item.approvalStatus === "待教师审批").length;
  const approved = requests.filter((item) => item.approvalStatus === "已批准").length;
  return (
    <section className="approval-page">
      <div className="approval-hero">
        <div><span className="eyebrow">LEAVE APPROVAL</span><h2>请假审批与汇总</h2><p>学生提交时自主选择审批教师，审批结果实时计入统一考勤。</p></div>
        {canCreate && <button className="primary" onClick={onCreate}>＋ {isTeacher ? "登记请假" : "新建请假申请"}</button>}
      </div>
      <div className="workflow-strip" aria-label="审批流程">
        <div className="workflow-step done"><i>1</i><span><strong>学生提交</strong><small>填写类型、日期和事由</small></span></div><b>→</b>
        <div className="workflow-step active"><i>2</i><span><strong>所选教师审批</strong><small>学生自主选择任课或导师教师</small></span></div><b>→</b>
        <div className="workflow-step"><i>3</i><span><strong>平台统一汇总</strong><small>出勤统计与报表自动更新</small></span></div>
      </div>
      <div className="approval-metrics">
        <article><span>待教师审批</span><strong>{pending}</strong><small>需及时处理</small></article>
        <article><span>本月已批准</span><strong>{approved}</strong><small>已纳入考勤口径</small></article>
        <article><span>平均审批时长</span><strong>1.8 <i>小时</i></strong><small>较上月缩短 24 分钟</small></article>
        <article><span>审批完成率</span><strong>94.6%</strong><small>目标 ≥ 95%</small></article>
      </div>
      <article className="panel approval-list">
        <div className="panel-head approval-head">
          <div><h3>{isTeacher ? "分配给我的审批" : "请假审批队列"}</h3><p>管理员可查看全量流程，教师仅能处理分配给自己的申请，学生只能查看本人申请</p></div>
          <div className="segmented"><button className={approvalFilter === "全部" ? "active" : ""} onClick={() => setApprovalFilter("全部")}>全部</button><button className={approvalFilter === "待教师审批" ? "active" : ""} onClick={() => setApprovalFilter("待教师审批")}>待审批</button><button className={approvalFilter === "已批准" ? "active" : ""} onClick={() => setApprovalFilter("已批准")}>已完成</button></div>
        </div>
        <div className="approval-table">
          <div className="approval-table-head"><span>申请学生</span><span>请假信息</span><span>审批教师</span><span>提交时间</span><span>流程状态</span><span>操作</span></div>
          {visible.map((item, index) => (
            <div className="approval-row" key={item.id}>
              <span className="person-cell"><i className={`avatar avatar-${index % 6}`}>{initials(item.personName)}</i><span><strong>{item.personName}</strong><small>{item.personId} · {item.unit}</small></span></span>
              <span><b className="leave-type">{item.type}</b><small className="subline">{item.start === item.end ? item.start : `${item.start}—${item.end}`}</small><small className="reason-line">{item.reason}</small></span>
              <span className="approver-cell"><i>{initials(item.approverName)}</i><span><strong>{item.approverName}</strong><small>{item.approverId}</small></span></span>
              <span>{item.createdAt}</span>
              <span><em className={`approval-status approval-${item.approvalStatus}`}>{item.approvalStatus}</em></span>
              <span className="decision-actions">{canDecide && item.approvalStatus === "待教师审批" ? <><button onClick={() => onDecision(item.id, "reject")}>驳回</button><button className="approve" onClick={() => onDecision(item.id, "approve")}>批准</button></> : <button className="detail">查看</button>}</span>
            </div>
          ))}
        </div>
      </article>
    </section>
  );
}

function Statistics({ people, period, setPeriod }: { people: Person[]; period: string; setPeriod: (v: string) => void }) {
  const groups = [
    { unit: "团队教师", role: "教师", rate: 98.1 },
    { unit: "2025级", role: "学生", rate: 95.8 },
    { unit: "2026级", role: "学生", rate: 94.9 },
  ];
  const ranking = [...people].sort((a, b) => b.rate - a.rate).slice(0, 8);
  const teacherPeople = people.filter((person) => person.role === "教师");
  const studentPeople = people.filter((person) => person.role === "学生");
  const teacherRate = teacherPeople.reduce((sum, person) => sum + person.rate, 0) / Math.max(teacherPeople.length, 1);
  const studentRate = studentPeople.reduce((sum, person) => sum + person.rate, 0) / Math.max(studentPeople.length, 1);
  const overallRate = people.reduce((sum, person) => sum + person.rate, 0) / Math.max(people.length, 1);
  return (
    <section className="stats-page">
      <div className="stats-hero"><div><span className="eyebrow">ATTENDANCE INSIGHTS</span><h2>出勤数据分析</h2><p>从整体趋势到入学年级，掌握团队教师与学生的出勤表现。</p></div><div className="period-switch"><button className="active">月度</button><button>本学期</button><select value={period} onChange={(e) => setPeriod(e.target.value)}><option>2026年8月</option><option>2026年7月</option><option>2026年6月</option></select><button className="secondary" onClick={() => window.print()}>⇩ 导出报告</button></div></div>
      <div className="stats-metrics">
        <article><span>整体出勤率</span><strong>{overallRate.toFixed(1)}%</strong><small className="up">团队成员综合数据</small></article>
        <article><span>教师出勤率</span><strong>{teacherRate.toFixed(1)}%</strong><small>累计请假 {teacherPeople.reduce((sum, person) => sum + person.leaveDays, 0)} 人次</small></article>
        <article><span>学生出勤率</span><strong>{studentRate.toFixed(1)}%</strong><small>累计请假 {studentPeople.reduce((sum, person) => sum + person.leaveDays, 0)} 人次</small></article>
        <article><span>有效考勤日</span><strong>19 <i>天</i></strong><small>应记录 {people.length * 19} 人次</small></article>
      </div>
      <div className="stats-grid">
        <article className="panel trend-panel"><div className="panel-head"><div><h3>近八周出勤趋势</h3><p>每周有效出勤人次占比</p></div><div className="legend"><span><i className="dot blue" />教师</span><span><i className="dot mint" />学生</span></div></div><MiniBars values={trend} /></article>
        <article className="panel target-panel"><div className="panel-head"><div><h3>学期目标</h3><p>截至第 3 考勤周</p></div></div><div className="big-donut" style={{ "--rate": `${overallRate}%` } as React.CSSProperties}><div><strong>{overallRate.toFixed(1)}%</strong><span>目标 95%</span></div></div><div className="target-note"><span>{overallRate >= 95 ? "已超过目标" : "距离目标"}</span><b>{overallRate >= 95 ? "+" : ""}{Math.abs(overallRate - 95).toFixed(1)}%</b></div></article>
        <article className="panel unit-panel"><div className="panel-head"><div><h3>各成员组出勤率</h3><p>团队教师及学生入学年级对比</p></div></div><div className="unit-bars">{groups.map((item) => <div key={item.unit}><span>{item.unit}<small>{item.role}</small></span><div><i className={item.role === "学生" ? "mint-bar" : ""}><em style={{ width: `${item.rate}%` }} /></i></div><b>{item.rate.toFixed(1)}%</b></div>)}</div></article>
        <article className="panel ranking-panel"><div className="panel-head"><div><h3>出勤表现</h3><p>模拟名单月度排名</p></div><span className="badge">TOP 8</span></div><div className="ranking-list">{ranking.map((p, i) => <div key={p.id}><b>{String(i + 1).padStart(2, "0")}</b><i className={`avatar avatar-${i % 6}`}>{initials(p.name)}</i><span><strong>{p.name}</strong><small>{p.unit}</small></span><em>{p.rate.toFixed(1)}%</em></div>)}</div></article>
      </div>
      <div className="stats-footnote"><span>i</span>统计口径：出勤率 = 有效出勤人次 ÷ 应出勤人次；已审批公假计入有效出勤，事假、病假单独列示。</div>
    </section>
  );
}
